import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirmStore } from '../../store/confirmStore';
import { Modal } from '../ui/Modal';
import { TYPE_NAMES_RU } from '../canvas/equipmentTypes';

const formatApiError = (err: any): string => {
  const detail = err.response?.data?.detail;
  if (!detail) {
    return err.message || 'Произошла неизвестная ошибка';
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((e: any) => {
      const locStr = e.loc ? ` (${e.loc.join('.')})` : '';
      return `${e.msg || 'Ошибка валидации'}${locStr}`;
    }).join('; ');
  }
  if (typeof detail === 'object') {
    if (detail.message) {
      return detail.message;
    }
    return JSON.stringify(detail);
  }
  return JSON.stringify(detail);
};

interface AddCableModalProps {
  dcCode: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onCableAdded: () => void;
}

export function AddCableModal({ dcCode, isOpen, onClose, onCableAdded }: AddCableModalProps) {
  const [allEquipment, setAllEquipment] = useState<any[]>([]);
  const [sourceEqId, setSourceEqId] = useState('');
  const [sourcePorts, setSourcePorts] = useState<any[]>([]);
  const [selectedSourcePortId, setSelectedSourcePortId] = useState('');
  const [destEqId, setDestEqId] = useState('');
  const [destPorts, setDestPorts] = useState<any[]>([]);
  const [selectedDestPortId, setSelectedDestPortId] = useState('');
  
  const [newCableType, setNewCableType] = useState('Cat6');
  const [newCableColor, setNewCableColor] = useState('blue');
  const [newCableStatus, setNewCableStatus] = useState('Live');
  const [newCableLength, setNewCableLength] = useState<string>('');
  
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Fetch all equipment in datacenter when modal is opened
  useEffect(() => {
    if (!isOpen || !dcCode) return;
    const fetchEquipment = async () => {
      try {
        const res = await api.get(`/api/datacenters/${dcCode}/equipment`);
        setAllEquipment(res.data || []);
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
      }
    };
    fetchEquipment();
  }, [isOpen, dcCode]);

  // Fetch ports when source equipment changes
  useEffect(() => {
    if (!sourceEqId) {
      setSourcePorts([]);
      setSelectedSourcePortId('');
      return;
    }
    const fetchSourcePorts = async () => {
      try {
        const res = await api.get(`/api/equipment/${sourceEqId}/ports`);
        setSourcePorts(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedSourcePortId(res.data[0].id);
        } else {
          setSelectedSourcePortId('');
        }
      } catch (err) {
        console.error('Failed to fetch source ports:', err);
      }
    };
    fetchSourcePorts();
  }, [sourceEqId]);

  // Fetch ports when destination equipment changes
  useEffect(() => {
    if (!destEqId) {
      setDestPorts([]);
      setSelectedDestPortId('');
      return;
    }
    const fetchDestPorts = async () => {
      try {
        const res = await api.get(`/api/equipment/${destEqId}/ports`);
        setDestPorts(res.data || []);
        if (res.data && res.data.length > 0) {
          setSelectedDestPortId(res.data[0].id);
        } else {
          setSelectedDestPortId('');
        }
      } catch (err) {
        console.error('Failed to fetch destination ports:', err);
      }
    };
    fetchDestPorts();
  }, [destEqId]);

  const handleAddCable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSourcePortId || !selectedDestPortId) {
      setModalError('Пожалуйста, выберите порты источника и цели');
      return;
    }
    if (sourceEqId === destEqId) {
      setModalError('Невозможно скоммутировать прибор сам на себя');
      return;
    }

    setModalLoading(true);
    setModalError(null);

    const lengthVal = newCableLength ? parseFloat(newCableLength) : null;

    try {
      const payload = {
        port_a_id: selectedSourcePortId,
        port_b_id: selectedDestPortId,
        cable_type: newCableType,
        color: newCableColor,
        status: newCableStatus,
        length_m: lengthVal,
        force_override: false
      };

      await api.post('/api/cables/batch', [payload]);
      
      handleClose();
      toast.success('Соединение успешно создано!');
      onCableAdded();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (
        err.response?.status === 422 &&
        typeof detail === 'object' &&
        detail?.code === 'CONNECTOR_MISMATCH' &&
        detail?.force_override_allowed
      ) {
        const confirmed = await useConfirmStore.getState().confirm({
          title: 'Несовместимость разъемов',
          message: `Предупреждение: ${detail.message}\n\nПорт А: ${detail.port_a_connector}\nПорт Б: ${detail.port_b_connector}\n\nВсё равно создать соединение? (Force Override)`,
          confirmText: 'Всё равно создать',
          cancelText: 'Отмена'
        });
        if (confirmed) {
          try {
            const payloadOverride = {
              port_a_id: selectedSourcePortId,
              port_b_id: selectedDestPortId,
              cable_type: newCableType,
              color: newCableColor,
              status: newCableStatus,
              length_m: lengthVal,
              force_override: true
            };
            await api.post('/api/cables/batch', [payloadOverride]);
            handleClose();
            toast.success('Соединение успешно создано (Force Override применён).');
            onCableAdded();
          } catch (err2: any) {
            setModalError('Ошибка создания: ' + formatApiError(err2));
          }
        }
      } else {
        setModalError('Ошибка: ' + formatApiError(err));
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleClose = () => {
    setSourceEqId('');
    setDestEqId('');
    setNewCableLength('');
    setModalError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Добавить новое соединение" size="md">
      <form onSubmit={handleAddCable} className="space-y-4 text-xs font-mono">
        {modalError && (
          <div className="p-3 bg-red-500/10 border border-red-500/25 text-red-400 rounded-sm">
            {modalError}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {/* Source Eq */}
          <div>
            <label htmlFor="modal-source-eq" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Устройство-Источник (А)</label>
            <select
              id="modal-source-eq"
              value={sourceEqId}
              onChange={e => setSourceEqId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
              required
            >
              <option value="">-- Выберите устройство --</option>
              {allEquipment.map(eq => (
                <option key={eq.id} value={eq.id}>{eq.name} ({TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type})</option>
              ))}
            </select>
          </div>

          {/* Source Port */}
          <div>
            <label htmlFor="modal-source-port" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Порт А</label>
            <select
              id="modal-source-port"
              value={selectedSourcePortId}
              onChange={e => setSelectedSourcePortId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
              required
              disabled={!sourceEqId}
            >
              <option value="">-- Выберите порт --</option>
              {sourcePorts.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.connector_type || 'RJ45'})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Dest Eq */}
          <div>
            <label htmlFor="modal-dest-eq" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Устройство-Цель (Б)</label>
            <select
              id="modal-dest-eq"
              value={destEqId}
              onChange={e => setDestEqId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
              required
            >
              <option value="">-- Выберите устройство --</option>
              {allEquipment.map(eq => (
                <option key={eq.id} value={eq.id}>{eq.name} ({TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type})</option>
              ))}
            </select>
          </div>

          {/* Dest Port */}
          <div>
            <label htmlFor="modal-dest-port" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Порт Б</label>
            <select
              id="modal-dest-port"
              value={selectedDestPortId}
              onChange={e => setSelectedDestPortId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
              required
              disabled={!destEqId}
            >
              <option value="">-- Выберите порт --</option>
              {destPorts.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.connector_type || 'RJ45'})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Cable Type */}
          <div>
            <label htmlFor="modal-cable-type" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Тип кабеля</label>
            <select
              id="modal-cable-type"
              value={newCableType}
              onChange={e => setNewCableType(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
            >
              <option value="Cat6">Cat6 (Copper)</option>
              <option value="Cat6a">Cat6a (Copper)</option>
              <option value="OM4">OM4 (Fiber)</option>
              <option value="OS2">OS2 (Fiber)</option>
              <option value="DAC">DAC (Twinax)</option>
            </select>
          </div>

          {/* Cable Color */}
          <div>
            <label htmlFor="modal-cable-color" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Цвет кабеля</label>
            <select
              id="modal-cable-color"
              value={newCableColor}
              onChange={e => setNewCableColor(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
            >
              <option value="blue">Синий (LAN)</option>
              <option value="red">Красный (Mgmt)</option>
              <option value="yellow">Жёлтый (Fiber)</option>
              <option value="green">Зелёный (Storage)</option>
              <option value="purple">Фиолетовый</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Cable Status */}
          <div>
            <label htmlFor="modal-cable-status" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Статус</label>
            <select
              id="modal-cable-status"
              value={newCableStatus}
              onChange={e => setNewCableStatus(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
            >
              <option value="Live">Live (Активный)</option>
              <option value="Planned">Planned (Планируемый)</option>
              <option value="Faulted">Faulted (Сбойный)</option>
            </select>
          </div>

          {/* Length */}
          <div>
            <label htmlFor="modal-cable-length" className="block text-[10px] text-gray-400 uppercase font-bold mb-1">Длина (м)</label>
            <input
              id="modal-cable-length"
              type="number"
              step="0.1"
              min="0.1"
              placeholder="Например, 2.5"
              value={newCableLength}
              onChange={e => setNewCableLength(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-sm text-text-primary focus:border-accent outline-none"
            />
          </div>
        </div>

        <div className="border-t border-border pt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-border rounded-sm text-text-secondary hover:bg-bg-hover transition-colors font-sans font-medium"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={modalLoading}
            className="px-4 py-2 bg-accent text-white rounded-sm hover:bg-accent-hover transition-colors disabled:opacity-50 font-sans font-medium"
          >
            {modalLoading ? 'Создание...' : 'Создать соединение'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
